import React, { useEffect, useRef } from 'react';
import { EventBus } from '../../../shared/EventBus';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { gameLogger } from '../../../utils/GameLogger';
import { resolveDevPresetName } from '../../../config/environment/devConfig';
import type { SimLiveDigest } from '../../../shared/sim/SimLiveDigest';
import { bootSearchParams } from '../../../config/boot/bootUrl';

/**
 * FollowLeaderController — passive, no-UI React component that wires
 * the F-key to a "follow current damage leader" camera mode for
 * spectators. Mounts beside `SimLiveHUD`; activates only under the
 * `sim-spectator` preset (or when forced via `?followLeader=1`).
 *
 * Why a React component (not an IGameSystem)?  React owns the DOM
 * keyboard layer (chat input focus, modal escape, etc.) and the
 * SimLiveHUD already subscribes to `net:message:simLive` from the same
 * tree. Folding the controller into the same tree avoids a
 * service-locator round-trip + duplicate subscription. The controller
 * still uses ServiceRegistry to grab CameraSystem + RemotePlayerManager
 * — those are Phaser-side, so they belong in the registry.
 *
 * Behavior:
 *   - F (when not typing in an input) toggles follow mode.
 *   - When ON, every `simLive` digest inspects `topDamage[0]`. If it's
 *     a different sid than what we're currently following, swap the
 *     CameraManager target. If the current leader dies, fall through
 *     to next-alive in the digest; if no candidates, fall back to
 *     local player.
 *   - When OFF, restores the camera to the local player sprite.
 *
 * The controller respects InputSystem's focus guard convention: it
 * skips its own listener when an INPUT/TEXTAREA/contentEditable element
 * has focus, so chat-typing doesn't toggle the follow mode.
 */

const SHOULD_INSTANTIATE_PARAM_KEY = 'followLeader';

function shouldInstantiate(): boolean {
  if (typeof window === 'undefined') return false;
  if (resolveDevPresetName() === 'sim-spectator') return true;
  try {
    const params = bootSearchParams();
    if (params.get(SHOULD_INSTANTIATE_PARAM_KEY) === '1') return true;
  } catch { /* ignore */ }
  return false;
}

function isInputFocused(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  return (
    el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.tagName === 'SELECT' ||
    el.isContentEditable
  );
}

const FollowLeaderControllerInner: React.FC = () => {
  const isFollowingRef = useRef(false);
  const followedSidRef = useRef<string | null>(null);

  useEffect(() => {
    const bus = EventBus.getInstance();
    const lastDigestRef: { current: SimLiveDigest | null } = { current: null };

    const onDigest = (digest: SimLiveDigest) => {
      lastDigestRef.current = digest;
      if (!isFollowingRef.current) return;
      const next = pickFollowTargetSid(digest);
      if (next && next !== followedSidRef.current) {
        if (applyFollow(next)) {
          followedSidRef.current = next;
        }
      } else if (!next && followedSidRef.current !== null) {
        // No live target — fall back to local player.
        restoreLocal();
        followedSidRef.current = null;
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'KeyF') return;
      if (isInputFocused()) return;
      e.preventDefault();
      isFollowingRef.current = !isFollowingRef.current;
      gameLogger.info('camera', `📸 [FollowLeader] toggle → ${isFollowingRef.current ? 'ON' : 'OFF'}`);
      if (!isFollowingRef.current) {
        restoreLocal();
        followedSidRef.current = null;
      } else {
        // Best-effort: pick from the most-recent digest if we have one;
        // otherwise the next digest will resolve the target.
        const last = lastDigestRef.current;
        if (last) {
          const target = pickFollowTargetSid(last);
          if (target && applyFollow(target)) {
            followedSidRef.current = target;
          }
        }
      }
    };

    bus.on('net:message:simLive', onDigest as any);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      bus.off('net:message:simLive', onDigest as any);
      window.removeEventListener('keydown', onKeyDown);
      // Don't auto-restore-local on unmount — user might be on a
      // different scene already; leave the camera where it is.
    };
  }, []);

  return null;
};

/** Pick the first alive bot in topDamage as the follow target. Returns
 *  null if no alive candidate exists. Exported for unit tests. */
export function pickFollowTargetSid(digest: SimLiveDigest): string | null {
  for (const row of digest.topDamage) {
    if (row.alive && row.sid) return row.sid;
  }
  return null;
}

function applyFollow(sid: string): boolean {
  const cam = ServiceRegistry.getInstance().get('CameraSystem');
  // RemotePlayerManager is OWNED by EntitySystem — not a top-level
  // ServiceRegistry entry. Resolve through EntitySystem to avoid a
  // perma-miss warning on lookup.
  const entitySystem = ServiceRegistry.getInstance().get('EntitySystem');
  const remote = entitySystem?.getRemotePlayerManager?.();
  const sprite = remote?.getSprite?.(sid);
  if (!cam || !sprite) {
    gameLogger.warn('camera', `📸 [FollowLeader] applyFollow(${sid}) — sprite missing (cam=${!!cam}, remote=${!!remote})`);
    return false;
  }
  cam.getManager().startFollowing(sprite);
  return true;
}

function restoreLocal(): void {
  const cam = ServiceRegistry.getInstance().get('CameraSystem');
  if (!cam) return;
  const local = cam.getManager().getPlayerSprite();
  if (local) {
    cam.getManager().startFollowing(local);
  } else {
    cam.getManager().stopFollowing();
  }
}

export const FollowLeaderController: React.FC = () => {
  if (!shouldInstantiate()) return null;
  return <FollowLeaderControllerInner />;
};
